/**
 * O autocomplete de {@code @menção} de uma caixa de texto.
 *
 * <p>Existe porque a menção só funciona com o handle <b>exato</b>: "@andre" não avisa o
 * Aponte, e o texto fica lá parecendo que avisou. É a pior forma de falhar — silenciosa,
 * e do lado de quem escreveu, que sai achando que chamou alguém.
 *
 * <p>Mora fora do componente porque a caixa de comentário não é a única que aceita
 * menção: o campo de edição de um comentário existente usa o mesmo gesto.
 */
import { useState } from 'react';

import type { Membro } from '../api/tipos';

/** O que está sendo digitado depois de um arroba, se houver. */
export interface Alvo {
  /** Onde o "@" está no texto. */
  inicio: number;
  /** O que veio depois dele até o cursor. */
  parcial: string;
}

/**
 * O arroba que o cursor está editando.
 *
 * <p>Olha para trás a partir do cursor e para no primeiro espaço ou quebra de linha —
 * um handle não tem nenhum dos dois. Devolve nada quando o caractere antes do arroba é
 * palavra, que é o que impede "theo@goyazgames.com" de abrir a lista no meio de um
 * e-mail; é a mesma regra da regex do servidor, do outro lado da mesma decisão.
 */
export function alvoNoCursor(texto: string, cursor: number): Alvo | null {
  for (let i = cursor - 1; i >= 0; i--) {
    const c = texto[i];
    if (c === ' ' || c === '\n' || c === '\t') return null;
    if (c !== '@') continue;

    const antes = i > 0 ? texto[i - 1] : ' ';
    if (/[\w@]/.test(antes)) return null;
    return { inicio: i, parcial: texto.slice(i + 1, cursor) };
  }
  return null;
}

/**
 * Minúscula e sem acento.
 *
 * <p>Sem isto, "@andre" não acha "André Aponte" — e esse é literalmente o caso que
 * justificou casar por nome além do handle. Handle nunca tem acento; nome quase sempre
 * tem, e ninguém digita "@andré" com o acento antes de escolher da lista.
 */
function comparavel(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

/**
 * Quão bem alguém casa com o que foi digitado — menor é melhor.
 *
 * <p>A ordem importa mais do que parece. Casar por "contém" no nome é o que permite
 * achar o Aponte digitando "@andre", mas sozinho ele bagunça: com "@h", "Theo Restivo"
 * casa (tem um "h" no meio) e aparecia <b>antes</b> do Hiro. Quem digita "@h" está
 * começando a escrever um handle, não procurando a letra h num nome.
 */
function nota(m: Membro, busca: string): number {
  const handle = comparavel(m.handle);
  const nome = comparavel(m.nome);
  if (handle.startsWith(busca)) return 0;
  if (nome.startsWith(busca)) return 1;
  // Qualquer palavra do nome: "@aponte" acha "André Aponte".
  if (nome.split(/\s+/).some((parte) => parte.startsWith(busca))) return 2;
  if (nome.includes(busca)) return 3;
  return 9;
}

/** Quantos nomes a lista mostra. Mais que isso vira rolagem dentro de um popover. */
const NA_LISTA = 6;

/**
 * Quem aparece na lista, na ordem, para o que já foi digitado depois do arroba.
 *
 * <p>Exportada porque é a regra que decide se a sugestão ajuda ou atrapalha, e ela é
 * pura — dá para prendê-la em teste sem montar componente nenhum.
 */
export function candidatosPara(membros: Membro[], parcial: string): Membro[] {
  const busca = comparavel(parcial);
  return membros
    .map((m) => ({ m, n: nota(m, busca) }))
    .filter(({ n }) => n < 9)
    // Estável dentro da mesma nota: dois com a mesma qualidade de casamento saem na
    // ordem em que o time veio do servidor, que é por id. Sem isso a lista dançaria.
    .sort((a, b) => a.n - b.n)
    .slice(0, NA_LISTA)
    .map(({ m }) => m);
}

export function useSugestaoDeMencao(membros: Membro[]) {
  const [alvo, setAlvo] = useState<Alvo | null>(null);
  const [destacado, setDestacado] = useState(0);

  const candidatos = alvo ? candidatosPara(membros, alvo.parcial) : [];

  const aberta = alvo != null && candidatos.length > 0;

  /** Chamado a cada digitação: recalcula o alvo a partir do cursor. */
  function aoDigitar(campo: HTMLTextAreaElement | HTMLInputElement) {
    const novo = alvoNoCursor(campo.value, campo.selectionStart ?? 0);
    setAlvo(novo);
    setDestacado(0);
  }

  function fechar() {
    setAlvo(null);
    setDestacado(0);
  }

  /**
   * Troca o trecho digitado pelo handle escolhido e devolve o texto novo.
   *
   * <p>Termina com espaço: sem ele, o próximo caractere gruda no handle e desfaz a
   * menção que a pessoa acabou de escolher.
   */
  function escolher(texto: string, handle: string): { texto: string; cursor: number } {
    if (!alvo) return { texto, cursor: texto.length };
    const antes = texto.slice(0, alvo.inicio);
    const depois = texto.slice(alvo.inicio + 1 + alvo.parcial.length);
    const meio = `@${handle} `;
    fechar();
    return { texto: antes + meio + depois, cursor: antes.length + meio.length };
  }

  /**
   * As teclas que a lista consome quando está aberta.
   *
   * <p>Devolve o handle escolhido, {@code null} quando só navegou ou fechou, e
   * {@code undefined} quando a tecla não é dela — aí quem chamou segue o próprio
   * tratamento (Ctrl+Enter enviando, por exemplo).
   */
  function aoTeclar(e: React.KeyboardEvent): string | null | undefined {
    if (!aberta) return undefined;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setDestacado((i) => (i + 1) % candidatos.length);
      return null;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setDestacado((i) => (i - 1 + candidatos.length) % candidatos.length);
      return null;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      fechar();
      return null;
    }
    // Enter e Tab escolhem. Enter só quando a lista está aberta — fora dela ele
    // continua quebrando linha, que é o que uma caixa de texto longo deve fazer.
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      return candidatos[destacado].handle;
    }
    return undefined;
  }

  return { aberta, candidatos, destacado, setDestacado, aoDigitar, aoTeclar, escolher, fechar };
}
